import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sprout, Phone, Menu, X, MessageCircle, Mail } from "lucide-react";

const NAV_LINKS = [
  { label: "Über uns", href: "#ueber-uns" },
  { label: "Vorstand", href: "#vorstand" },
  { label: "Wasser", href: "#wasser" },
  { label: "Termine", href: "#termine" },
  { label: "Galerie", href: "#galerie" },
  { label: "Mitglied werden", href: "#mitglied-werden" },
  { label: "Kontakt", href: "#kontakt" },
  { label: "Forum", href: "/forum" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mt-4 flex items-center justify-between rounded-full border border-[#D8D3C5]/70 bg-[#F8F7F2]/85 backdrop-blur-md px-5 py-3 shadow-[0_8px_30px_rgba(26,56,43,0.08)]">
          <a href="#top" className="flex items-center gap-2.5" data-testid="nav-logo">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1A382B] text-[#F8F7F2]">
              <Sprout size={18} strokeWidth={1.8} />
            </span>
            <span className="font-display text-xl font-bold tracking-tight text-[#1A382B]">
              Schönwald <span className="hidden sm:inline font-jbm text-[10px] tracking-[0.3em] uppercase text-[#5A826B] align-middle ml-1">e.V.</span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-7" data-testid="nav-desktop">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                className="text-sm font-medium text-[#2A332E] transition-colors duration-300 hover:text-[#C85A32]"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setContactOpen(!contactOpen)}
                data-testid="nav-contact-button"
                className="inline-flex items-center gap-2 rounded-full bg-[#C85A32] px-4 py-2 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
              >
                <Phone size={15} /> Kontakt
              </button>
              <AnimatePresence>
                {contactOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setContactOpen(false)} data-testid="nav-contact-backdrop" />
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.97 }}
                      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute right-0 z-50 mt-3 w-72 rounded-2xl border border-[#D8D3C5] bg-white p-2 shadow-[0_20px_50px_rgba(26,56,43,0.18)]"
                      data-testid="nav-contact-menu"
                    >
                      <a href="tel:+4917684019206" data-testid="nav-contact-call" onClick={() => setContactOpen(false)} className="flex items-center gap-3 rounded-xl px-4 py-3 transition-colors hover:bg-[#EFECE4]">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E2ECE5] text-[#1A382B]">
                          <Phone size={16} />
                        </span>
                        <span>
                          <span className="block text-sm font-semibold text-[#1A382B]">Anrufen</span>
                          <span className="block font-jbm text-xs text-[#47544A]">0176 84019206</span>
                        </span>
                      </a>
                      <a href="https://wa.me/4917684019206" target="_blank" rel="noopener noreferrer" data-testid="nav-contact-whatsapp" onClick={() => setContactOpen(false)} className="flex items-center gap-3 rounded-xl px-4 py-3 transition-colors hover:bg-[#EFECE4]">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E2ECE5] text-[#1A382B]">
                          <MessageCircle size={16} />
                        </span>
                        <span>
                          <span className="block text-sm font-semibold text-[#1A382B]">WhatsApp-Nachricht</span>
                          <span className="block text-xs text-[#47544A]">Chat direkt öffnen</span>
                        </span>
                      </a>
                      <a href="mailto:info@kga-schoenwald.de" data-testid="nav-contact-mail" onClick={() => setContactOpen(false)} className="flex items-center gap-3 rounded-xl px-4 py-3 transition-colors hover:bg-[#EFECE4]">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E2ECE5] text-[#1A382B]">
                          <Mail size={16} />
                        </span>
                        <span>
                          <span className="block text-sm font-semibold text-[#1A382B]">E-Mail schreiben</span>
                          <span className="block text-xs text-[#47544A]">info@kga-schoenwald.de</span>
                        </span>
                      </a>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
            <button
              className="lg:hidden flex h-9 w-9 items-center justify-center rounded-full border border-[#D8D3C5] text-[#1A382B]"
              onClick={() => setOpen(!open)}
              data-testid="nav-mobile-toggle"
              aria-label="Menü öffnen"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden mx-4 mt-2 rounded-2xl border border-[#D8D3C5] bg-[#F8F7F2]/95 backdrop-blur-md p-4 shadow-xl"
            data-testid="nav-mobile-menu"
          >
            {[...NAV_LINKS, { label: "Wichtige Rufnummern", href: "#rufnummern" }].map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="block rounded-xl px-4 py-3 text-base font-medium text-[#1A382B] hover:bg-[#EFECE4]"
                data-testid={`nav-mobile-link-${l.href.slice(1)}`}
              >
                {l.label}
              </a>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
